import { differenceInDays, parse } from "date-fns";
import { currencySymbols } from "../config/constants";
// import { generateUniqueStringWithTime } from "./gigHelpers";

export const createFormData = (data) => {
  const formData = new FormData();
  // Append the object data to the FormData instance
  for (const key in data) {
    if (Array.isArray(data[key])) {
      // Handle array values (e.g., File objects)
      data[key].forEach((file, index) => {
        formData.append(`${key}`, file);
      });
    } else {
      // Append non-array values
      formData.append(key, data[key]);
    }
  }
  return formData;
};

export const getExcelDate = (inputDate) => {
  const excelEpoch = new Date(1899, 11, 30); // Excel epoch: Dec 30, 1899

  let date;

  // 1. Handle no input
  if (!inputDate) {
    date = new Date();
  }

  // 2. Handle Date object
  else if (inputDate instanceof Date) {
    date = inputDate;
  }

  // 3. Handle string input
  else if (typeof inputDate === "string") {
    const trimmed = inputDate.trim();

    // Case: 20230501 format (8 digits, no separator)
    if (/^\d{8}$/.test(trimmed)) {
      const year = parseInt(trimmed.substring(0, 4), 10);
      const month = parseInt(trimmed.substring(4, 6), 10);
      const day = parseInt(trimmed.substring(6, 8), 10);

      date = new Date(year, month - 1, day); // Month is 0-indexed
    }

    // Case: YYYY-MM-DD or YYYY/MM/DD
    else if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(trimmed)) {
      date = new Date(trimmed);
    }

    // Try fallback parsing
    else {
      date = new Date(trimmed);
    }
  }

  // 4. Unsupported input
  else {
    throw new Error("Invalid input: must be a string or Date object.");
  }

  // 5. Final date validation
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date provided: "${inputDate}".`);
  }

  // 6. Calculate Excel serial number
  const diffInDays = Math.floor(
    (date.getTime() - excelEpoch.getTime()) / (1000 * 60 * 60 * 24)
  );
  return diffInDays;
};

export const getISOFormattedDate = (offsetDays = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays); // Adjust the date by offsetDays
  return date.toISOString(); // Convert to ISO format
};

export const formatOmanDate = (dateStr) => {
  if (dateStr) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${day}/${month}/${year}`;
  }
  return "N/A";
};

export const prepareSelectData = (dataArray, name, currentLanguage) => {
  let options = [];

  if (dataArray && dataArray?.length) {
    dataArray.forEach((data, index) => {
      switch (name) {
        case "country_code":
          options.push({
            label: data?.country_mobile_code,
            value: data?.country_mobile_code,
          });
          break;
        case "country":
          options.push({
            label:
              currentLanguage === "ar"
                ? data?.name_ar || data?.name
                : data?.name_en || data?.name,
            value: data.id,
          });
          break;
        case "travel_product_type":
          options.push({
            label:
              currentLanguage === "ar"
                ? data?.name_ar || data?.name
                : data?.name_en || data?.name,
            value: data.id,
          });
          break;
        case "plate_type":
          options.push({
            label: data?.plate_char_en || data?.name,
            value: data.id,
          });
          break;
        case "admeid":
          options.push({
            label: data?.description,
            value: data?.admeid,
          });
          break;
        default:
          options.push({ label: data.option_label, value: data.option_value });
      }
    });
  }
  return options;
};

/**
 * Get the difference in days between two full Date strings.
 * @param {string} dateStr1 - e.g. "Thu May 15 2025 00:00:00 GMT+0530"
 * @param {string} dateStr2 - e.g. "Wed May 21 2025 00:00:00 GMT+0530"
 * @returns {number} Number of days (positive)
 */
export const getDaysDiff = (dateStr1, dateStr2) => {
  const date1 = new Date(dateStr1);
  const date2 = new Date(dateStr2);

  // Normalize time to midnight
  date1.setHours(0, 0, 0, 0);
  date2.setHours(0, 0, 0, 0);

  const diffMs = Math.abs(date2 - date1);
  return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
};

/**
 * Format a full JavaScript date string like "Thu May 15 2025..." to "dd/mm/yyyy".
 * @param {string} inputStr - Full date string.
 * @returns {string} Formatted as "dd/mm/yyyy".
 */
export const formatToDDMMYYYY = (inputStr) => {
  const date = new Date(inputStr);

  if (isNaN(date)) return "Invalid Date";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // month is 0-indexed
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const downloadPdf = async (data) => {
  try {
    const {
      name = "document.pdf",
      mimeType = "application/pdf",
      content,
    } = data;

    if (!content) {
      throw new Error("No content provided");
    }

    // Convert base64 to Blob
    const byteCharacters = atob(content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Create a link and trigger the download
    const link = document.createElement("a");
    const blobUrl = window.URL.createObjectURL(blob);
    link.href = blobUrl;
    link.download = name;
    document.body.appendChild(link); // Required for Firefox
    link.click();
    document.body.removeChild(link);

    // Clean up
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

export const capitalize = (str) => {
  if (!str) return "";
  // console.log("Before:: ", str);
  // console.log("After:: ", str.charAt(0).toUpperCase() + str.slice(1).toLowerCase());

  return str.charAt(0).toUpperCase() + str?.slice(1)?.toLowerCase();
};

export const getAgeFromDOB = (dobStr) => {
  const [day, month, year] = dobStr.split("/").map(Number);
  const dob = new Date(year, month - 1, day); // Month is 0-based in JS
  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--; // Birthday hasn't occurred yet this year
  }

  return age;
};

export const setVehicleInformation = (vehicleData) => {
  return {
    dateofBirth: `${vehicleData?.ropLicense?.Body?.GetLicenseInfoResponse?.GetLicenseInfoResult?.DateofBirth?.slice(
      0,
      4
    )}-${vehicleData?.ropLicense?.Body?.GetLicenseInfoResponse?.GetLicenseInfoResult?.DateofBirth?.slice(
      4,
      6
    )}-${vehicleData?.ropLicense?.Body?.GetLicenseInfoResponse?.GetLicenseInfoResult?.DateofBirth?.slice(
      6
    )}`,
    dateFirstIssued: `${vehicleData?.ropLicense?.Body?.GetLicenseInfoResponse?.GetLicenseInfoResult?.DateFirstIssued?.slice(
      6
    )}`,
    drivingLicenseIssueDate:
      vehicleData?.ropLicense?.Body?.GetLicenseInfoResponse
        ?.GetLicenseInfoResult?.DateFirstIssued,
    vehicleChassisNumber:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.ChassisNumber,
    // vehicleChassisNumber: generateUniqueStringWithTime(),
    // plateColor: vehicleData?.ropVehicle?.Body
    //   ?.GetVehicleValuationByPlateResponse
    //   ?.GetVehicleValuationByPlateResult?.VehicleInfo?.ColorDesc,
    plateCode:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.PlateCode,
    plateNumber:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.PlateNumber,
    platType:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.RegistraionType,
    licenseNo:
      vehicleData?.ropLicense?.Body?.GetLicenseInfoResponse
        ?.GetLicenseInfoResult?.LicenseNumber,
    makeCode:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.VehicleMakeID,
    modelCode:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.VehicleModelID,
    modelYear:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.MakeYear,
    registrationMonth:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse?.GetVehicleValuationByPlateResult?.VehicleInfo?.RegistrationIssueDate?.substring(
        4,
        6
      ),
    // noClaimDiscountInYears: vehicleData?.ropVehicle?.Body
    //   ?.GetVehicleValuationByPlateResponse
    //   ?.GetVehicleValuationByPlateResult?.Claims
    //   ?.Claimdetails?.length,
    noClaimDiscountInYears: "5",
    makeYear:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.MakeYear,
    vehicleModelId:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.VehicleModelID,
    makeYearDate: vehicleData?.ropVehicle?.Body
      ?.GetVehicleValuationByPlateResponse?.GetVehicleValuationByPlateResult
      ?.VehicleInfo?.MakeYear
      ? `${vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse?.GetVehicleValuationByPlateResult?.VehicleInfo?.MakeYear}-01-01`
      : "",
    insuranceEndDate: `${vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse?.GetVehicleValuationByPlateResult?.VehicleInfo?.InsuranceEndDate.slice(
      0,
      4
    )}-${vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse?.GetVehicleValuationByPlateResult?.VehicleInfo?.InsuranceEndDate.slice(
      4,
      6
    )}-${vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse?.GetVehicleValuationByPlateResult?.VehicleInfo?.InsuranceEndDate.slice(
      6
    )}`,
    registraionType:
      vehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
        ?.GetVehicleValuationByPlateResult?.VehicleInfo?.RegistraionType,
  };
};

export const getCoverName = (companyData, cover) => {
  let name = "";
  if (companyData && companyData?.company_id === 1) {
    name = cover?.cover_description + " " + (cover?.cover_coverage || "");
  } else if (companyData && companyData?.company_id === 4) {
    if (cover?.sumInsured?.amount && cover?.sumInsured?.amount > 0) {
      name =
        cover?.name +
        " " +
        currencySymbols[cover?.sumInsured?.currencyCode] +
        cover?.sumInsured?.amount;
    } else {
      name = cover?.name;
    }
  } else if (companyData && companyData?.company_id === 5) {
    name = cover;
  } else if (companyData && companyData?.company_id === 2) {
    if (cover?.sumInsured && cover?.sumInsured === "0") {
      name = cover?.name;
    } else {
      name = cover?.name + " " + `${cover?.sumInsured == null ? '' : cover?.sumInsured || ""}`;
    }
  }
  return name;
};

export const isTodayOrPast = (input) => {
  // Parse "YYYYMMDD"
  const year = parseInt(input.substring(0, 4), 10);
  const month = parseInt(input.substring(4, 6), 10) - 1; // JS months are 0-based
  const day = parseInt(input.substring(6, 8), 10);

  const inputDate = new Date(year, month, day);

  // Get today's date without time
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return inputDate.getTime() <= today.getTime();
};

// compute validation flags directly from vehicle data (synchronous)
export const computeVehicleValidationFlags = (
  currentVehicleData,
  selectedProduct
) => {
  const flags = {
    previousInsuranceExpired: false,
    licenceExpired: false,
    vehicleAgeLimitExpired: false,
    previousThirdPartyInsurance: false,
    policyHas60Days: false,
  };

  if (!currentVehicleData) return flags;

  // vehicle info path (same as your useEffect)
  const vehicleInfo =
    currentVehicleData?.ropVehicle?.Body?.GetVehicleValuationByPlateResponse
      ?.GetVehicleValuationByPlateResult?.VehicleInfo;

  if (vehicleInfo?.InsuranceEndDate) {
    // isTodayOrPast expects date in whatever format your helper already handles
    const isInsuranceEndDatePast = isTodayOrPast(vehicleInfo?.InsuranceEndDate);
    flags.previousInsuranceExpired = isInsuranceEndDatePast;

    // compute days diff for >60 rule (matching your useEffect)
    const today = new Date();
    const targetDate = parse(
      vehicleInfo?.InsuranceEndDate,
      "yyyyMMdd",
      new Date()
    );
    const diff = differenceInDays(targetDate, today);
    if (diff > 60) flags.policyHas60Days = true;
  }

  // vehicle age check (MakeYear)
  if (
    selectedProduct?.product_id === 1 &&
    selectedProduct?.sub_product_id === 21 &&
    vehicleInfo?.MakeYear
  ) {
    const vehicleAge =
      new Date().getFullYear() - parseInt(vehicleInfo?.MakeYear, 10);
    if (vehicleAge > 7) flags.vehicleAgeLimitExpired = true;
  }

  // previous insurance type check
  if (
    selectedProduct?.product_id === 1 &&
    selectedProduct?.sub_product_id === 21 &&
    vehicleInfo?.InsuranceTypeDesc
  ) {
    if (vehicleInfo?.InsuranceTypeDesc.toLowerCase().includes("third")) {
      flags.previousThirdPartyInsurance = true;
    }
  }

  // license expiry
  const licenceInfo =
    currentVehicleData?.ropLicense?.Body?.GetLicenseInfoResponse
      ?.GetLicenseInfoResult;
  if (licenceInfo?.DateofExpiry) {
    flags.licenceExpired = isTodayOrPast(licenceInfo?.DateofExpiry);
  }

  return flags;
};
